import styled, { css } from 'styled-components';

import EnterprisePlanIcon from '../../../assets/icons/EnterprisePlanIcon';
import theme from '../../../assets/theme';

/**
 * Flat rounded-square chip used on home-page cards to mark School-plan
 * content. Part of the A3 tier-accent system: pale orange tint with the
 * Curipod crown in brand orange. Pairs with `FreePlanAccentIcon` — when a
 * card is available on both tiers, `RankedCard` renders a fused pill
 * instead.
 */
export type SchoolPlanAccentIconProps = {
  size?: number;
  state?: 'active' | 'muted';
  className?: string;
};

export function SchoolPlanAccentIcon({
  size = 22,
  state = 'active',
  className,
}: SchoolPlanAccentIconProps) {
  const iconPx = Math.round(size * 0.6);
  return (
    <Chip $size={size} $state={state} className={className} aria-label="School plan">
      <EnterprisePlanIcon
        color={state === 'active' ? theme.colors.orange : theme.colors.locked}
        width={iconPx}
        height={iconPx}
        style={{ display: 'block' }}
      />
    </Chip>
  );
}

const Chip = styled.span<{ $size: number; $state: 'active' | 'muted' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 5px;
  flex: 0 0 auto;

  /*
   * Re-assert the glyph's own size. Buttons in this codebase commonly style
   * their descendant icons (e.g. "svg { width: 15px; margin-right: 10px }"),
   * which has the same specificity as a plain "& > svg" here and would win on
   * source order — sizing the crown wrong and shoving it off-centre inside the
   * chip. The doubled "&&" raises specificity so the chip always wins.
   */
  && > svg {
    width: ${({ $size }) => Math.round($size * 0.6)}px;
    height: ${({ $size }) => Math.round($size * 0.6)}px;
    margin: 0;
    flex: 0 0 auto;
  }

  ${({ $state }) =>
    $state === 'active'
      ? css`
          background: ${theme.colors.lightOrangeBg};
        `
      : css`
          background: ${theme.colors.lockedBg};
        `}

  /*
   * Subtle hover animation — scale up slightly and add a soft glow so the
   * chip feels interactive when the teacher hovers over it (pairs with the
   * AntD Tooltip wrapped around it by the consumer).
   */
  transition: transform 180ms ease, box-shadow 180ms ease;
  transform-origin: center;
  will-change: transform;

  &:hover {
    transform: scale(1.2);
    box-shadow: ${({ $state }) =>
      $state === 'active'
        ? '0 2px 8px rgba(249, 151, 70, 0.32)'
        : '0 2px 8px rgba(183, 174, 162, 0.3)'};
  }
`;
